import { Component } from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from "react-redux"
import { bindActionCreators } from 'redux'
import { addFlashMessageOfType } from 'redux-flash-messages'
import { actions as employeeActions } from '../../../../actions/employee'
import Moment from 'moment'

import { ARIA_TABLE_ROLE, setLocationOfPriorFocus } from '../common'
import { LoadingButton } from '../LoadingButton'

import { Flyout, FlyoutContent } from '@amzn/stencil-react-components/flyout'
import { Modal, ModalContent } from '@amzn/stencil-react-components/modal'
import { Col, Flex, Hr, Row } from '@amzn/stencil-react-components/layout'
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Text } from '@amzn/stencil-react-components/text'
import { FormWrapper, InputWrapper,LabelPosition} from '@amzn/stencil-react-components/form'
import { Radio } from '@amzn/stencil-react-components/form'
import { IconInformationFill, IconSize } from '@amzn/stencil-react-components/icons'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { ScreenReaderOnly } from '@amzn/stencil-react-components/screen-reader-only'
import { LiveAnnouncer } from '@amzn/stencil-react-components/live-announcer'
import { H3 } from '@amzn/stencil-react-components/text'

import employeeService from '../../../../services/Employee.service'

const RADIO_BUTTON_GROUP_PREFIX = 'radio-button';
const CASH_REFUND_OPTION_ID_PREFIX = 'cash-refund';
const FRACTIONAL_SHARES_OPTION_ID_PREFIX = 'fractional-shares';

class FractionalSharesDialog extends Component {
    constructor(props) {
        super(props);
        this.state = this.getDefaultState();
      }
      getDefaultState = () => {
        let vests = [];
        let employee = {};
        const defaultVests = employeeService.getUpcomingVests(this.props.employee.awards);
        defaultVests.forEach((vest) => {
            vests.push({
                id: vest.id,
                fractionalShareIndicator: vest.fractional_share_in,
                fractional_share_eligible_in: vest.fractional_share_eligible_in,
                vest_date: vest.vest_date,
                vest_shares: vest.vest_shares,
                award_id: vest.award_id,
                award_name: vest.award_name,
                tax_election: vest.tax_election
            });
        });

        employee = {
            person_id: this.props.employee.id,
            fractional_share_in: this.props.employee.fractional_share_in
        };

        const startingState = {
            vests: vests,
            employee: employee,
            error: false,
            isFractionalElectionInformationModalVisible: false,
            loading: false
        };
        return startingState;

      };

      getEffectiveElection = (t, vest) => {
        if (!vest.fractional_share_eligible_in) {
            return "** " + t('FractionalSharesDialog.NotApplicable');
        }

        if (!(vest.tax_election && vest.tax_election.code)) {
            return "";
        }

        if (vest.tax_election.code === "1" || vest.tax_election.code === "2"){
            return "* " + t(`TaxTreatment.${vest.tax_election.code}`);
        }

        return t(`TaxTreatment.${vest.tax_election.code}`);
      };

    createFractionalElectionOptions = (t, vest, flexDirection = "column") => {
        const cashRefundOptionId = vest ? `${CASH_REFUND_OPTION_ID_PREFIX}-${vest.id}` : CASH_REFUND_OPTION_ID_PREFIX;
        const fractionalSharesOptionId = vest ? `${FRACTIONAL_SHARES_OPTION_ID_PREFIX}-${vest.id}` : FRACTIONAL_SHARES_OPTION_ID_PREFIX;
        const radioButtonGroup = vest ? `${RADIO_BUTTON_GROUP_PREFIX}-${vest.id}` : RADIO_BUTTON_GROUP_PREFIX;

        const vestAwardName = vest ? vest.award_name : "";
        const vestDate = vest ? vest.vest_date : "";

        const fractionalSharesChecked = vest ? vest.fractionalShareIndicator : this.state.employee.fractional_share_in;
        const selectionDisabled = vest ? !vest.fractional_share_eligible_in : false;

        return (
            <fieldset role="radiogroup">
                <legend>
                    <ScreenReaderOnly>{t('FractionalSharesDialog.HeaderFractionalElection') + " " +  vestAwardName + " " + vestDate}</ScreenReaderOnly>
                </legend>
                <FormWrapper rowGap="S300">
                    <Flex gridGap="S300" flexDirection={flexDirection}>
                        <InputWrapper
                            id={cashRefundOptionId}
                            labelText={t('FractionalSharesDialog.HeaderCashRefund')}
                            labelPosition={LabelPosition.Trailing}>
                            {() => 
                                <Radio
                                    dataTestId="cashRefundOption_radio"
                                    name={radioButtonGroup} 
                                    id={cashRefundOptionId} 
                                    checked={!fractionalSharesChecked} 
                                    onClick={() => this.onFractionElectionOptionSelect(vest, false)} 
                                    onChange={()=>{}}
                                    disabled={selectionDisabled}
                                />
                            }
                        </InputWrapper>
                        <InputWrapper
                            id={fractionalSharesOptionId}
                            labelText={t('FractionalSharesDialog.HeaderFractionalShares')}
                            labelPosition={LabelPosition.Trailing}>
                            {() => 
                                <Radio
                                    dataTestId="fractionalSharesOption_radio"
                                    name={radioButtonGroup}  
                                    id={fractionalSharesOptionId} 
                                    checked={fractionalSharesChecked} 
                                    onClick={() => this.onFractionElectionOptionSelect(vest, true)} 
                                    onChange={()=>{}}
                                    disabled={selectionDisabled}
                                />
                            }
                        </InputWrapper>
                    </Flex>  
                </FormWrapper>
            </fieldset>
        );
    };

    createDialogRow = (vestCol, fractionalElectionCol, taxElectionCol, ariaRoleList, alignItems) => {
        return (
            <Row role={ARIA_TABLE_ROLE.Row} gridGap="S300" alignItems={alignItems}>
                <Col width="30%" role={ariaRoleList[0]}>{vestCol}</Col>
                <Col width="48%" role={ariaRoleList[1]}>{fractionalElectionCol}</Col>
                <Col width="22%" role={ariaRoleList[2]}>{taxElectionCol}</Col>
            </Row>
        );
    };

    createDialogHeader = (t) => {
        const vestHeader = <Text>{t('FractionalSharesDialog.HeaderVestingEvent')}</Text>;
        const fractionalElectionHeader = <Text>{t('FractionalSharesDialog.HeaderFractionalElection')}</Text>;
        const taxElectionHeader = (<Text>{t('FractionalSharesDialog.HeaderTaxElection')}</Text>);
        return (
            <>
                {this.createDialogRow(
                    vestHeader,
                    fractionalElectionHeader,
                    taxElectionHeader,
                    [ARIA_TABLE_ROLE.ColumnHeader, ARIA_TABLE_ROLE.ColumnHeader, ARIA_TABLE_ROLE.ColumnHeader],
                    "center"
                )}
                <Hr/>
            </>    
        );
    };

    createVestRow = (t, vest) => {
        const vestCell = (
            <>
                <Text color="neutral70" fontSize="T100">{Moment(vest.vest_date).format('ll')}</Text>
                <Text dataTestId="number_of_shares" fontWeight="bold">{new Intl.NumberFormat(this.props.locale).format(vest.vest_shares)}&nbsp;{t('FractionalSharesDialog.SharesVesting')}</Text>
                <Text color="neutral70" fontSize="T100">{vest.award_id}</Text>
                <Text color="neutral70" fontSize="T100">({vest.award_name})</Text>
            </>
        );

        const fractionalElectionCell = this.createFractionalElectionOptions(t, vest);
        const taxElectionCell = <Text>{this.getEffectiveElection(t, vest)}</Text>;
        return (
            <>
                {this.createDialogRow(
                    vestCell, 
                    fractionalElectionCell, 
                    taxElectionCell, 
                    [ARIA_TABLE_ROLE.RowHeader, ARIA_TABLE_ROLE.Cell, ARIA_TABLE_ROLE.Cell]
                )}
                <Hr/>
            </>
        );
    }

    onFractionElectionOptionSelect = (targetVest, fractionalSharesCheck) => {
        const employee = this.state.employee;
        const vests = this.state.vests;

        // if no target vest is passed, we assumed the change applies to all vests in future awards (but only for vest eligible for fractional share)
        if (!targetVest) {
            employee.fractional_share_in = fractionalSharesCheck;
            vests.forEach(vest => {
                if (vest.fractional_share_eligible_in) {
                    vest.fractionalShareIndicator = fractionalSharesCheck;
                }
            });
            this.setState({
                employee: employee,
                vests: vests
            });
            return;
        }

        // otherwise, the change will only apply the specified vest
        vests.forEach(vest => {
            if (vest.id == targetVest.id && vest.fractional_share_eligible_in) {
                vest.fractionalShareIndicator = fractionalSharesCheck;
            }
        });
        this.setState({
            vests: vests
        });
    };

    onDialogClose = () => {
        this.setState(this.getDefaultState()); // reset all states in this dialog before closing
        this.props.onDialogClose();
    };

    onSaveButtonClick = (t) => {
        this.setState({loading: true})
        const employee = this.state.employee;
        const vests = this.state.vests;
        this.props.actions.employee.applyFractionalIndicators(employee, vests).then( _ => {
            this.props.onDialogClose();
            addFlashMessageOfType("SUCCESS", false, t('FractionalSharesDialog.FractionalShareElectionsApplied'), null, setLocationOfPriorFocus(this.props.refFrom, this.props.idFrom));
        }).catch( _ => {
            this.setState({
                error: true,
                loading: false
            });
        });
    };

    onFractionalElectionInformationIconClick = () => {
        this.setState({
            isFractionalElectionInformationModalVisible: true
        });
    };

    onFractionalElectionInformationModalClose = () => {
        this.setState({
            isFractionalElectionInformationModalVisible: false
        });
    };

    onErrorMessageClose = () => {
        this.setState({
            error: false
        });
    };

    render() {
        if (!this.props.visible) {
            return null;
        }

        const { t } = this.props;
        return (
            <>
                <Flyout dataTestId="FractionalSharesDialog_flyout" isOpen={true} close={this.onDialogClose}>
                    <LiveAnnouncer>
                        <FlyoutContent
                            dataTestId='FractionalSharesDialog_FlyoutContent'
                            titleText={t('PopularActions.FractionalSharesButton')}
                            onCloseButtonClick={this.props.onDialogClose}
                            closeButtonAltText='Close Button for Fractional Shares Dialog'
                            buttons={[
                                <LoadingButton
                                    key="dialogSaveButton"
                                    variant={ButtonVariant.Primary}
                                    onClick={() => this.onSaveButtonClick(t)}
                                    text={t('FractionalSharesDialog.Save')}
                                    loading={this.state.loading}
                                />
                            ]}
                        >
                            <Col gridGap="S400">
                                {this.state.error && 
                                    <MessageBanner
                                        dataTestId="FractionalSharesDialog_error_banner"
                                        isDismissible={true}
                                        dismissButtonAltText="Dismiss Fractional Shares Dialog Failed Message"
                                        type={MessageBannerType.Error}
                                        onDismissed={this.onErrorMessageClose}
                                        aria-live="polite"
                                    >
                                        {t('FractionalSharesDialog.FailedToApply')}
                                    </MessageBanner>
                                }
                                <Col gridGap="S300">
                                    <H3 fontWeight="medium">
                                        {t('FractionalSharesDialog.AllCurrentAndFutureVests')}
                                        <Button
                                                dataTestId="onFractionalElectionInformationIcon_button"
                                                style={{display: "inline"}}
                                                icon={<IconInformationFill title={t('FractionalSharesDialog.HeaderFractionalElectionInfoIcon')} size={IconSize.ExtraSmall}/>}
                                                onClick={this.onFractionalElectionInformationIconClick}
                                                variant={ButtonVariant.Tertiary}
                                        />
                                    </H3>
                                    {this.createFractionalElectionOptions(t, null, "row")}
                                </Col>
                                <Col gridGap="S300">
                                    <H3 fontWeight="medium">{t('FractionalSharesDialog.CurrentVests')}</H3>
                                    <MessageBanner
                                        type={MessageBannerType.Informational}
                                        aria-live='polite'
                                    >
                                        <Text>{t('FractionalSharesDialog.AllCurrentAndFutureVestsElectionReminder')}</Text>
                                    </MessageBanner>
                                    <Col gridGap="S300" role={ARIA_TABLE_ROLE.Table} aria-label={t('PopularActions.FractionalSharesButton')}>
                                        {this.createDialogHeader(t)}
                                        {this.state.vests.map((vest) => 
                                            this.createVestRow(t, vest)
                                        )}
                                    </Col>
                                </Col>
                                <Col margin={{top: "S300"}} gridGap="S300">
                                    <Text color="neutral70" fontSize="T100">{t('FractionalSharesDialog.OnlyApply')}</Text>
                                    <Text color="neutral70" fontSize="T100">{t('FractionalSharesDialog.GreyedOut')}</Text>
                                </Col>
                            </Col>
                        </FlyoutContent>
                    </LiveAnnouncer>
                </Flyout>

                <Modal dataTestId="FractionalSharesDialog_modal"
                       isOpen={this.state.isFractionalElectionInformationModalVisible}
                       close={this.onFractionalElectionInformationModalClose}>
                    <ModalContent 
                        titleText={t('FractionalSharesDialog.HeaderFractionalElectionInfoIcon')}
                        buttons={[
                            <Button dataTestId="FractionalElectionInformationModalClose_button"
                                    key="informationModal" variant={ButtonVariant.Primary}
                                    onClick={this.onFractionalElectionInformationModalClose}>{t('GenericDialog.CloseButton')}</Button>
                        ]}
                    >
                        <Col gridGap="S400">
                            <Col key="cash-refund-info" gridGap="S200" >
                                <Text role="heading" aria-level="3" fontWeight="bold">
                                  {t('FractionalSharesDialog.HeaderCashRefund')}
                                </Text>
                                <Text>{t('FractionalSharesDialog.HeaderCashRefundInfo')}</Text>
                            </Col>
                            <Col key="fractional-shares-info" gridGap="S200">
                                <Text role="heading" aria-level="3" fontWeight="bold">
                                  {t('FractionalSharesDialog.HeaderFractionalShares')}
                                </Text>
                                <Text>{t('FractionalSharesDialog.HeaderFractionalSharesInfo')}</Text>
                            </Col>
                        </Col>
                    </ModalContent>
                </Modal>
            </>
        );
    }
}

const mapState = (state) => ({
    employee: state.employee,
});

const mapDispatchToProps = dispatch => ({
    actions: {
      employee: bindActionCreators(employeeActions, dispatch)
    }
});

export default connect(mapState, mapDispatchToProps)(withTranslation('translationsV2')(FractionalSharesDialog));
